<template>
  <van-cell is-link
            :title="$t('printerModel')"
            @click="show = true"
            style="text-align: left"
  >
    <template #right-icon>
      <div style="display: flex; align-items: center;">
        <span style="color: #6E62F0;margin-right: 2px;">{{ selectedLabel }}</span>
        <span class="custom-arrow"></span>
      </div>
    </template>
  </van-cell>
  <van-action-sheet
      v-model:show="show"
      :actions="actions"
      :title="$t('description')"
      @select="onSelect"
      close-on-click-action
  >
  </van-action-sheet>
  <van-tabs v-model:active="active" swipeable shrink class="tutorial-tab">
    <van-tab :title="$t('video')">
      <van-popup
          v-model:show="isVideoPlaying"
          position="center"
          closeable
          @closed="onPopupClosed"
          class="video-popup"
      >
        <video
            controls
            ref="videoRef"
            :src="currentVideoUrl"
            autoplay
            muted
            playsinline
            class="portrait-video"
        >
          <source :src="currentVideoUrl" type="video/webm">
        </video>
      </van-popup>
      <!-- 分类标题循环 -->
      <div v-for="category in categories" :key="category.title">
        <div v-if="category.items.length > 0" class="category-title">{{ $t(category.title) }}</div>
        <van-grid :border="false" :column-num="2">
          <van-grid-item v-for="(item) in category.items" :key="item.key">
            <template #icon>
              <div class="video-thumbnail" @click="showVideo(item)" style="pointer-events: auto">
                <van-image :src="item.thumbnail"/>
                <div class="play-button">
                  <van-icon name="play-circle-o" size="40"/>
                </div>
              </div>
            </template>
            <template #text>
              <div class="video-title" :title="$t(item.title)">{{ $t(item.title) }}</div>
            </template>
          </van-grid-item>
        </van-grid>
      </div>
    </van-tab>
    <!-- 产品说明书 -->
    <van-tab :title="$t('product')">
      <div class="container" style="height: 100vh; overflow-y: auto;">
        <div class="d-flex flex-column justify-content-start align-items-center h-100 container-top">
          <b-img v-for="id in imageCount"
                 :key="id"
                 v-bind="mainProps"
                 :src="getImageUrl(id)"
                 loading="lazy"
                 :alt="`Page${id}`"
                 class="img-fluid"
                 @dragstart.prevent>
          </b-img>
        </div>
      </div>
    </van-tab>

    <van-tab :title="$t('question')">
      <div v-for="q in filteredQuestions" :key="q.name">
        <van-collapse v-model="activeNames">
          <van-collapse-item
              :title="q.title"
              :name="q.name"
              :class="{ expanded: isExpanded(q.name) }"
              style="text-align: left;font-size: 15px; font-weight: 500;"
          >
            <template #right-icon>
              <div style="display: flex; align-items: center;">
                <span class="custom-arrow"></span>
              </div>
            </template>
<!--            <div class="answer-text">{{ q.content }}</div>-->
            <div class="answer-text" v-html="renderHtmlContent(q.content)"></div>
          </van-collapse-item>
        </van-collapse>
      </div>
    </van-tab>
  </van-tabs>
</template>

<script>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import {useI18n} from "vue-i18n";

export default {
  setup() {
    const route = useRoute();
    const show = ref(false);
    const {locale} = useI18n();
    
    // 修改actions为computed属性，根据appDomain参数动态过滤机型
    const actions = computed(() => {
      const allActions = [
        {name: 'N12'},
        {name: 'N12A'},
        {name: 'N15'},
        {name: 'N20'},
        {name: 'N21'},
        {name: 'N31'},
        {name: 'N40'},
        {name: 'N80'},
        {name: 'D20'},
        {name: 'D20A'},
        {name: 'D21'},
        {name: 'D80'}
      ];
      
      // 获取appDomain参数，如果没有则默认为1（过滤掉N12A和D20A）
      const appDomain = route.query.appDomain || '1';
      
      // 如果appDomain为1，则过滤掉N12A和D20A
      if (appDomain === '1') {
        return allActions.filter(action =>
            action.name !== 'N12A' && action.name !== 'D20A'
        );
      }
      
      // 默认返回所有机型
      return allActions;
    });
    
    // 确保默认选中的机型在当前过滤后的列表中存在
    const getDefaultEquipment = () => {
      // 检查route.query.equipment是否在可用列表中
      const equipment = route.query.equipment || 'N20';
      const isEquipmentAvailable = actions.value.some(action => action.name === equipment);
      
      // 如果指定的设备不可用，则使用第一个可用设备作为默认值
      return isEquipmentAvailable ? equipment : actions.value[0].name;
    };
    
    const selectedAction = ref(getDefaultEquipment());
    const selectedLabel = computed(() => selectedAction.value);
    const onSelect = (item) => {
      selectedAction.value = item.name;
      show.value = false; // 关闭 action sheet
    }
    const renderHtmlContent = (content) => {
      // 创建一个临时的div元素用于解析HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // 遍历所有元素节点，只保留允许的标签
      const allowedTags = ['SPAN', 'STRONG', 'B', 'I', 'EM'];
      const allElements = tempDiv.querySelectorAll('*');

      allElements.forEach(element => {
        if (!allowedTags.includes(element.tagName)) {
          element.replaceWith(...element.childNodes);
        } else if (element.tagName === 'SPAN') {
          // 只保留style属性中的color和font-weight
          const allowedStyles = ['color', 'font-weight'];
          const style = element.getAttribute('style');
          if (style) {
            const styles = style.split(';').filter(s => {
              const property = s.split(':')[0].trim();
              return allowedStyles.includes(property);
            }).join(';');
            element.setAttribute('style', styles);
          }
        }
      });

      return tempDiv.innerHTML;
    };
    // 监听路由变化，包括appDomain和equipment参数的变化
    watch(
        [() => route.query.appDomain, () => route.query.equipment],
        () => {
          // 更新选中的设备
          selectedAction.value = getDefaultEquipment();
        }
    );
    
    const active = ref(0);
    // 监听路由变化，更新语言
    watch(
        () => route.query.lang,
        (newLang) => {
          if (newLang && ['en', 'zh', 'zh-Hant', 'de', 'fr', 'it', 'es', 'ja', 'ko'].includes(newLang)) {
            locale.value = newLang
          } else if (newLang && newLang.startsWith('zh')) {
            locale.value = 'zh';
          } else if (newLang === '' || newLang === null || newLang === undefined) {
            const browserLang = (navigator.language || 'en-US').split('-')[0];
            newLang = ['en', 'zh'].includes(browserLang) ? browserLang : 'en'
            locale.value = newLang;
          } else {
            locale.value = 'en';
          }
        },
        {immediate: true}
    );
    watch(
        () => active.value,
        () => {
          if (isVideoPlaying.value) {
            isVideoPlaying.value = false;
            // 可选：停止视频播放
            if (videoRef.value) {
              videoRef.value.pause();
            }
          }
        }
    );

    // ----------- 视频教程 -----------------
    const items = ref([]);

    onMounted(() => {
      fetch('/data/video.json')
          .then(res => res.json())
          .then(data => {
            items.value = data;
          })
          .catch(err => {
            console.error('加载视频数据失败:', err);
          });
    });
    // 新增分类数据结构
    const categories = computed(() => [
      {
        title: 'categoryTypes.product',
        items: filteredItems.value.filter(item => item.category === 'product')
      },
      {
        title: 'categoryTypes.question',
        items: filteredItems.value.filter(item => item.category === 'question')
      }
    ]);
    // 视频播放相关
    const isVideoPlaying = ref(false);
    const currentVideoUrl = ref('');
    const videoRef = ref(null);
    const filteredItems = computed(() => {
      // 韩语目前只有 N15 说明书图片，视频教程统一回退到英文
      const currentLang = locale.value === 'ko' ? 'en' : locale.value;
      const currentModel = selectedAction.value;
      return items.value.filter(item =>
          item.language.includes(currentLang) &&
          item.models.includes(currentModel)
      );
    });
    const showVideo = (item) => {
      // 重新播放同一个视频
      // 先清空
      console.log('Video URL:', item.videoUrl);
      setTimeout(() => {
        currentVideoUrl.value = item.videoUrl;
        isVideoPlaying.value = true;
      }, 0);
    };


    onUnmounted(() => {
      // 清理视频资源
      if (videoRef.value) {
        videoRef.value.pause();
        videoRef.value.src = '';
        videoRef.value.load();
      }

      // 清理响应式引用
      filteredItems.value = [];
    });

    const onPopupClosed = () => {
      // 停止视频播放
      if (videoRef.value) {
        videoRef.value.pause();
      }
    };
    // ------------- 视频教程 end -------------------


    // ------------- 常見問題 -----------------------
    const activeNames = ref(['1']);
    const questions = ref([]);
    const isExpanded = (name) => {
      const active = activeNames.value;
      if (Array.isArray(active)) {
        return active.includes(name);
      }
      return active === name;
    };

    watch(
        () => currentVideoUrl.value,
        (newUrl) => {
          if (newUrl && videoRef.value) {
            // 视频加载完成后重新计算transform值
            videoRef.value.addEventListener('loadedmetadata', () => {
              // 这里可以触发重新计算，但Vue会自动响应变化
            });
          }
        }
    );
    onMounted(() => {
      Promise.all([
        fetch('/data/faq-domain.json').then(res => res.json()),
        fetch('/data/faq-ko.json').then(res => res.json())
      ])
          .then(([data, koreanData]) => {
            questions.value = [...data, ...koreanData];
          })
          .catch(err => {
            console.error('加载 FAQ 数据失败', err);
          });
    });

    const filteredQuestions = computed(() => {
      const currentLang = locale.value;
      const currentModel = selectedAction.value;
      const appDomain = route.query.appDomain || '1';
      // N31 暂时共用 N21 的常见问题，N12A 与 N12 共用常见问题
      const faqModels = currentModel === 'N31'
          ? ['N31', 'N21']
          : currentModel === 'N12A'
              ? ['N12']
              : [currentModel];

      return questions.value.filter(q =>
          q.language.includes(currentLang) &&
          faqModels.some(model => q.models.includes(model)) &&
          q.appDomain && q.appDomain.includes(appDomain)
      );
    });
    //  ------------- 常見問題 end ------------------


    // ------------- 產品説明書 --- -----------------
    const mainProps = {
      center: true,
      fluidGrow: true,
      width: 600,
      height: 400,
    };

    const transformValue = computed(() => {
      const deviceHeight = window.innerHeight;
      console.log(deviceHeight);
      if (deviceHeight < 600) {
        return 'translate(0%, 40%)';
      } else if (deviceHeight < 700) {
        return 'translate(0%, 7%)';
      } else if (deviceHeight < 900) {
        return 'translate(0%, 9%)';
      } else if (deviceHeight < 950) {
        return 'translate(0%, 10%)';
      }else if (deviceHeight < 1100) {
        return 'translate(0%, 12%)';
      }else if (deviceHeight < 1400) {
        return 'translate(0%, 15%)';
      } else {
        return 'translate(0%, 5%)';
      }
    });

    const imageCount = computed(() => {
      const equipment = selectedAction.value || 'N20';
      const manualEquipment = equipment === 'N12A' ? 'N12' : equipment;
      const requestedLang = locale.value || 'en';
      // N12A 复用 N12 说明书，N31 的所有非英语说明书统一使用英文图片
      const lang = equipment === 'N31'
          ? 'en'
          : requestedLang === 'ko' && equipment !== 'N15'
              ? 'en'
              : requestedLang;

      // 定义每种设备和语言组合的图片数量
      const imageCounts = {
        'D20': {
          'en': 11,
          'zh': 11,
          'zh-Hant': 11,
          'de': 5,
          'fr': 5,
          'it': 5,
          'es': 5,
          'ja': 9
        },
        'D21': {
          'en': 6,
          'zh': 6,
          'zh-Hant': 6,
          'de': 5,
          'fr': 5,
          'it': 5,
          'es': 5,
          'ja': 6
        },
        'D80': {
          'en': 23,
          'zh': 23,
          'zh-Hant': 23,
          'de': 23,
          'fr': 23,
          'it': 23,
          'es': 23,
          'ja': 23
        },
        'N12': {
          'en': 11,
          'zh': 11,
          'zh-Hant': 11,
          'de': 9,
          'fr': 9,
          'it': 9,
          'es': 10,
          'ja': 8
        },
        'N12A': {
          'en': 10,
          'zh': 10,
          'zh-Hant': 10,
          'de': 10,
          'fr': 10,
          'it': 10,
          'es': 10,
          'ja': 10
        },
        'N20': {
          'en': 13,
          'zh': 13,
          'zh-Hant': 13,
          'de': 11,
          'fr': 11,
          'it': 11,
          'es': 11,
          'ja': 13
        },
        'N31': {
          'en': 20,
          'zh': 20,
          'zh-Hant': 20,
          'de': 20,
          'fr': 20,
          'it': 20,
          'es': 20,
          'ja': 20
        },
        'N80': {
          'en': 24,
          'zh': 24,
          'zh-Hant': 24,
          'de': 11,
          'fr': 11,
          'it': 11,
          'es': 11,
          'ja': 24
        },
        'N40':{
          'en': 10,
          'zh': 10,
          'zh-Hant': 10,
          'de': 10,
          'fr': 10,
          'it': 10,
          'es': 10,
          'ja': 9
        },
        'N15':{
          'en': 8,
          'zh': 8,
          'zh-Hant': 8,
          'de': 7,
          'fr': 7,
          'it': 7,
          'es': 7,
          'ja': 8,
          'ko': 10
        },
        'N21':{
          'en': 13,
          'zh': 13,
          'zh-Hant': 13,
          'de': 13,
          'fr': 13,
          'it': 13,
          'es': 13,
          'ja': 12
        },
      };

      // 返回对应设备和语言的图片数量，如果找不到则返回默认值0
      return imageCounts[manualEquipment] && imageCounts[manualEquipment][lang]
          ? imageCounts[manualEquipment][lang]
          : 0;
    });

    const getImageUrl = (id) => {
      const equipment = selectedAction.value || 'N20';
      const manualEquipment = equipment === 'N12A' ? 'N12' : equipment;
      const requestedLang = locale.value || 'en';
      const lang = equipment === 'N31'
          ? 'en'
          : requestedLang === 'ko' && equipment !== 'N15'
              ? 'en'
              : requestedLang;
      return `https://d3j9vygjocoacx.cloudfront.net/guide/${manualEquipment}-images/${lang}/${id}-zip.jpg`;
      // return `https://cloudfront.nadain.com/guide/${equipment}-images/${lang}/${id}-zip.jpg`;
    };
    // ------------- 產品説明書 end -----------------
    return {
      show,
      actions,
      selectedAction,
      selectedLabel,
      onSelect,
      active,
      items,
      isVideoPlaying,
      currentVideoUrl,
      videoRef,
      showVideo,
      onPopupClosed,
      filteredItems,
      categories,
      activeNames,
      isExpanded,
      filteredQuestions,
      imageCount,
      getImageUrl,
      mainProps,
      transformValue,
      renderHtmlContent
    };
  },
};

</script>
<style scoped>
:deep(.van-cell__right-icon) {
  display: none !important; /* 隐藏默认图标 */
}

:deep(.van-overlay) {
  background: rgb(0 0 0 / 55%);
}

.custom-arrow {
  display: inline-block;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 0 6px 9px; /* 向右的三角形 */
  border-color: transparent transparent transparent #999; /* 实心颜色 */
  margin-left: 8px;
}

.van-collapse-item.expanded .custom-arrow {
  transform: rotate(90deg); /* 向下箭头 */
}

.category-title {
  font-size: 14px;
  margin: 16px 16px 0;
  text-align: left;
}

.video-thumbnail {
  position: relative;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  padding: 10px;
}
.video-popup{
  position: relative !important;
}
.video-title {
  font-size: 14px;
  color: #666;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-wrap: break-word; /* 允许长词换行到下一行 */
  word-break: break-all; /* 强制文本在边界内换行 */
  text-overflow: ellipsis;
}

.answer-text {
  white-space: pre-wrap; /* 保留空格和换行符，并允许文本自动换行 */
  text-align: left;
}

:deep(.van-tabs__nav) {
  gap: 15px !important;
}

:deep(.van-tab--active) {
  color: #6E62F0 !important;
}

:deep(.van-tabs__line) {
  background: #6E62F0 !important;
  width: 22px !important;
}

:deep(.van-popup--center) {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  box-sizing: border-box;
  max-width: 100vw;
  max-height: 100vh;
  transform: v-bind(transformValue) !important;
}

.portrait-video {
  width: auto;
  height: 70vh;
  max-width: 90vw;
  max-height: 70vh;
  object-fit: contain;
  aspect-ratio: 9/16;
}
</style>
